import React from "react";

import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";

import {
  COLOR_KICK_BLUE,
  COLOR_KICK_BLUE_LIGHTENED,
} from "~/shared/sharedConstants";

import {
  kickBlue,
  kickLightBlue,
  kickLightPink,
  kickNavy,
  kickPink,
  kickRed,
} from "~/shared/styles/baseStyles";

import imgFruitPunch from "~assets/images/flavors/tropical-fruit-medley-flavor.png";

function StyledButton({ children, style, ...props }) {
  return (
    <Button
      color="primary"
      size="large"
      style={{
        backgroundColor: COLOR_KICK_BLUE,
        color: "white",
        padding: "11px 22px",
        whiteSpace: "nowrap",
        ...style,
      }}
      {...props}
    >
      {children}
    </Button>
  );
}

export function FlavoredBBsUpsellCard({ onClick }) {
  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        textAlign: { xs: "center", sm: "left" },
        gap: "16px",
        background: "#fff",
        border: "1px solid #ececf4",
        borderRadius: "16px",
        overflow: "hidden",
        boxShadow: "0 6px 20px -6px rgba(30,35,71,0.16)",
        padding: { xs: "16px", sm: "22px 24px 18px" },
        marginTop: "24px",
        marginBottom: "14px",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          bottom: 0,
          width: "4px",
          background: `linear-gradient(180deg, ${kickRed} 0%, ${kickPink} 100%)`,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "center", sm: "flex-start" },
          gap: "16px",
        }}
      >
        <img
          src={imgFruitPunch}
          alt="Faster & Stronger Beta-Blocker Mints"
          style={{
            width: "76px",
            height: "76px",
            objectFit: "contain",
            flexShrink: 0,
            borderRadius: "14px",
            background: `linear-gradient(135deg, ${kickLightBlue} 0%, ${kickLightPink} 100%)`,
            padding: "8px",
            boxShadow: "0 4px 12px -4px rgba(30,35,71,0.22)",
          }}
        />
        <Box sx={{ flex: 1, minWidth: 0, width: { xs: "100%", sm: "auto" } }}>
          <span
            style={{
              display: "inline-block",
              fontFamily: "BasisGrotesquePro-Bold",
              fontSize: "11.5px",
              letterSpacing: "0.05em",
              textTransform: "uppercase",
              color: "#fff",
              background: `linear-gradient(135deg, ${kickRed} 0%, ${kickPink} 100%)`,
              borderRadius: "999px",
              padding: "3px 10px",
              marginBottom: "8px",
              boxShadow: "0 3px 8px -2px rgba(242,89,48,0.45)",
            }}
          >
            Now Available
          </span>
          <div
            style={{
              fontFamily: "BasisGrotesquePro-Bold",
              fontSize: "17px",
              color: kickNavy,
              marginBottom: "4px",
            }}
          >
            Faster &amp; Stronger Beta-Blocker Mints: 5x Faster in Three Great
            Flavors
          </div>
          <div
            style={{
              fontSize: "13.5px",
              color: "#555",
              lineHeight: 1.45,
            }}
          >
            Our new compounded sublingual mints start working in just 10
            minutes.
          </div>
        </Box>
      </Box>
      <div
        style={{
          borderRadius: "10px",
          padding: "12px 14px",
          backgroundColor: kickLightPink,
          fontFamily: "BasisGrotesquePro-Regular",
        }}
      >
        <ul
          style={{
            fontFamily: "BasisGrotesquePro-Regular",
            textAlign: "left",
            margin: "0",
            paddingLeft: "20px",
          }}
        >
          <li>Dissolves under tongue in 60 seconds</li>
          <li>Same trusted meds, 20% stronger dosage</li>
          <li>Faster-acting for unexpected moments</li>
        </ul>
        <p
          style={{
            fontFamily: "BasisGrotesquePro-Bold",
            marginTop: "10px",
            marginBottom: 0,
          }}
        >
          Start visit below to order today
        </p>
      </div>
      <StyledButton
        onClick={onClick}
        sx={{
          alignSelf: { xs: "stretch", sm: "flex-start" },
          width: { xs: "100%", sm: "auto" },
          background: `linear-gradient(135deg, ${kickBlue} 0%, ${COLOR_KICK_BLUE_LIGHTENED} 100%)`,
          boxShadow: "0 6px 16px -4px rgba(63,63,130,0.45)",
          transition: "filter 0.15s ease",
          "&:hover": {
            filter: "brightness(1.08)",
          },
        }}
      >
        Start Visit&nbsp;
        <NavigateNextIcon />
      </StyledButton>
    </Box>
  );
}

// How far ProductPhoto zooms in on each family of product photos. The photos
// are 1080×1080 flat-lays on white with the product centered, so shown whole
// the product reads small and the row is padded by the photo's own white
// margins: tablets and lozenges occupy ~59%×28% of the canvas, the 12-tablet
// sample pack ~83%×27%, and the sleep bottle shots ~54–69%×51–64%. Each scale
// fills its frame without clipping the product or its shadow.
export const PRODUCT_PHOTO_SCALE_TABLETS = 1.5;
export const PRODUCT_PHOTO_SCALE_SAMPLE_PACK = 1.1;
export const PRODUCT_PHOTO_SCALE_SLEEP = 1.2;

/**
 * Product image for the flavor / product selection rows.
 *
 * The photo sits in a fixed-ratio frame that clips whatever the zoom pushes
 * outside it. Flat-lay photos on white (pass a `scale` > 1, see the
 * PRODUCT_PHOTO_SCALE_* constants) are zoomed in on their centered product and
 * multiply-blended so their white background disappears into the selected
 * (cream) row instead of showing as a white box. Transparent, tightly-cropped
 * artwork such as the regular-tablet packs takes no `scale` and is shown whole.
 */
export function ProductPhoto({ src, alt, scale, aspectRatio = "2 / 1", sx }) {
  const zoom = scale > 1;
  return (
    <Box
      sx={{
        flexShrink: 0,
        width: { xs: "100%", sm: "240px" },
        maxWidth: "240px",
        aspectRatio,
        overflow: "hidden",
        marginBottom: { xs: "16px", sm: 0 },
        ...sx,
      }}
    >
      <Box
        component="img"
        src={src}
        alt={alt}
        sx={{
          display: "block",
          width: "100%",
          height: "100%",
          objectFit: zoom ? "cover" : "contain",
          transform: zoom ? `scale(${scale})` : "none",
          mixBlendMode: zoom ? "multiply" : "normal",
        }}
      />
    </Box>
  );
}

/**
 * The one seam between the two arms of the product-flavor-photos experiment.
 *
 * Control renders master's product image byte for byte: the legacy file in a fixed
 * 200×200 <img>, a direct child of the row so the row's "& > img" rules still apply,
 * plus whatever per-screen inline extras master had (`legacyStyle`: the sleep
 * screen's border radius, the sleep trial's object-fit). Test renders ProductPhoto
 * with the new file. Rows whose file did not change pass only `src`.
 *
 * When the experiment ends, replace every <FlavorPhoto> with the winning branch and
 * delete this component.
 */
export function FlavorPhoto({
  reframed,
  src,
  legacySrc,
  legacyStyle,
  alt,
  scale,
  aspectRatio,
}) {
  if (reframed) {
    return (
      <ProductPhoto
        src={src}
        alt={alt}
        scale={scale}
        aspectRatio={aspectRatio}
      />
    );
  }
  return (
    <img
      src={legacySrc ?? src}
      style={{
        position: "relative",
        width: "200px",
        height: "200px",
        ...legacyStyle,
      }}
      alt={alt}
    />
  );
}
